import type { ChartDatum } from '../charts/chartTypes';
import type { IFactTable, IMonthFacts, IPeriodFacts } from '../types/metrics';
import type { MonthKey } from '../types/period';
import type { MetricTileId, ReportComponentId } from '../types/report';
import { MetricsHelper } from './MetricsHelper';
import { MonthKeyHelper } from './MonthKeyHelper';
import { ReportFormatHelper } from './ReportFormatHelper';

export type MetricTileFormat = 'currency' | 'months';

export interface IMetricTileSpec {
  value: (facts: IPeriodFacts) => number | null;
  /** Per-month value for the trend chart. */
  monthly: (facts: IMonthFacts, table: IFactTable) => number | null;
  secondary?: (facts: IPeriodFacts, currency: string) => string | null;
  /** Whether an increase should render as favourable. */
  upIsGood: boolean;
  format: MetricTileFormat;
}

export interface IMetricTileDelta {
  delta: number | null;
  pct: number | null;
  /** null when there is no movement to judge. */
  favourable: boolean | null;
}

const compact = (value: number | null, currency: string) =>
  ReportFormatHelper.currency(value, currency, { compact: true });

/** Value pickers and display rules for each KPI tile. */
export class MetricTileHelper {
  private static readonly SPECS: Record<MetricTileId, IMetricTileSpec> = {
    revenue: {
      value: (f) => f.pnl.revenue,
      monthly: (m) => m.pnl.revenue,
      upIsGood: true,
      format: 'currency',
    },
    gross_profit: {
      value: (f) => f.pnl.grossProfit,
      monthly: (m) => m.pnl.grossProfit,
      secondary: (f) =>
        `${ReportFormatHelper.percent(f.pnl.grossMargin)} margin`,
      upIsGood: true,
      format: 'currency',
    },
    ebitda: {
      value: (f) => f.pnl.ebitda,
      monthly: (m) => m.pnl.ebitda,
      secondary: (f) =>
        `${ReportFormatHelper.percent(f.pnl.ebitdaMargin)} margin`,
      upIsGood: true,
      format: 'currency',
    },
    net_profit: {
      value: (f) => f.pnl.netProfit,
      monthly: (m) => m.pnl.netProfit,
      secondary: (f) => `${ReportFormatHelper.percent(f.pnl.netMargin)} margin`,
      upIsGood: true,
      format: 'currency',
    },
    opex: {
      value: (f) => f.pnl.opex,
      monthly: (m) => m.pnl.opex,
      secondary: (f) =>
        f.pnl.revenue
          ? `${ReportFormatHelper.percent(f.pnl.opex / f.pnl.revenue)} of revenue`
          : null,
      upIsGood: false,
      format: 'currency',
    },
    cash: {
      value: (f) => f.balance.cashAndInvestments,
      monthly: (m) => m.balance.cashAndInvestments,
      secondary: (f, currency) =>
        `cash ${compact(f.balance.cash, currency)} · inv ${compact(f.balance.investments, currency)}`,
      upIsGood: true,
      format: 'currency',
    },
    net_burn: {
      value: (f) => (f.cashFlow.hasOpening ? f.cashFlow.netBurn : null),
      monthly: (m) => (m.cashFlow.hasOpening ? m.cashFlow.netBurn : null),
      secondary: (f, currency) =>
        f.trailing3Burn === null
          ? null
          : `T3 avg ${compact(f.trailing3Burn, currency)}/mo`,
      upIsGood: false,
      format: 'currency',
    },
    runway: {
      value: (f) => f.runwayMonths,
      monthly: (m, table) =>
        MetricsHelper.runwayFromBurn(
          m.balance.cashAndInvestments,
          MetricsHelper.trailing3Burn(table, m.month)
        ),
      secondary: (f) =>
        f.trailing3Burn !== null && f.trailing3Burn <= 0
          ? 'cash-flow positive on T3 basis'
          : 'cash + inv ÷ T3 avg burn',
      upIsGood: true,
      format: 'months',
    },
  };

  static spec(id: MetricTileId): IMetricTileSpec {
    return MetricTileHelper.SPECS[id];
  }

  /** One datum per month, labelled for the x-axis; `category` is the series key. */
  static trend(
    id: MetricTileId,
    table: IFactTable,
    months: MonthKey[],
    category: string
  ): ChartDatum[] {
    const spec = MetricTileHelper.SPECS[id];
    return MetricsHelper.monthlySeries(table, months, (m) =>
      spec.monthly(m, table)
    ).map(({ month, value }) => ({
      label: MonthKeyHelper.shortLabel(month),
      [category]: value,
    }));
  }

  private static readonly TILE_BY_COMPONENT: Partial<
    Record<ReportComponentId, MetricTileId>
  > = {
    revenue_tile: 'revenue',
    gross_profit_tile: 'gross_profit',
    ebitda_tile: 'ebitda',
    net_profit_tile: 'net_profit',
    opex_tile: 'opex',
    cash_tile: 'cash',
    net_burn_tile: 'net_burn',
    runway_tile: 'runway',
  };

  /** Maps a `*_tile` component id to its metric, or null for non-tile components. */
  static tileIdFor(componentId: ReportComponentId): MetricTileId | null {
    return MetricTileHelper.TILE_BY_COMPONENT[componentId] ?? null;
  }

  static delta(
    spec: IMetricTileSpec,
    value: number | null,
    prior: number | null
  ): IMetricTileDelta {
    const delta = value !== null && prior !== null ? value - prior : null;
    const pct =
      value !== null && prior !== null && prior !== 0
        ? (value - prior) / Math.abs(prior)
        : null;
    const favourable =
      delta === null || delta === 0 ? null : delta > 0 === spec.upIsGood;
    return { delta, pct, favourable };
  }

  static formatValue(
    spec: IMetricTileSpec,
    value: number | null,
    currency: string
  ): string {
    return spec.format === 'months'
      ? ReportFormatHelper.months(value)
      : compact(value, currency);
  }

  static formatDelta(
    spec: IMetricTileSpec,
    delta: number | null,
    currency: string
  ): string {
    if (spec.format === 'currency')
      return ReportFormatHelper.delta(delta, currency);
    if (delta === null) return '—';
    return `${delta > 0 ? '+' : ''}${delta.toFixed(1)} mo`;
  }
}
